import * as React from 'react';
import { selector } from '@glitz/core';
import { styled } from '@glitz/react';
import { type NumberFieldPropType, useCombineEventHandlers, useNumberField } from '@avensia/excite-ui';
import { Button } from '../button';
import { Minus, Plus, ArrowUp, ArrowDown } from '../icons';
import { Input, ValidationMessage } from './text-field';
import { Theme, type ThemeType } from 'webapp/theme';
import { FieldSize, useFieldSize, type FieldSizeType } from '../field-size';
import { translate } from '@avensia/excite';
import { NnTestIdProps } from 'webapp/shared/test-ids';

type PropType = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'size'> &
  NumberFieldPropType & {
    value: number;
    as?: ThemeType;
    size?: FieldSizeType;
    compact?: boolean;
  };

export type { PropType as NumberFieldPropType };

export const NumberField = styled(
  React.forwardRef(
    (
      {
        value,
        min,
        max,
        onDebouncedUpdate,
        onReduce,
        onAdd,
        size,
        error,
        validate,
        compact,
        disabled,
        as,
        ...restProps
      }: PropType,
      ref: React.Ref<HTMLInputElement>,
    ) => {
      const elementRef = React.useRef<HTMLInputElement>(null);
      React.useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => elementRef.current);

      const { add, reduce, invalid, validationMessage, inputProps } = useNumberField(elementRef, value, {
        error,
        validate,
        min,
        max,
        onDebouncedUpdate,
        onReduce,
        onAdd,
      });

      const onChange = useCombineEventHandlers(restProps.onChange, inputProps.onChange);
      const onBlur = useCombineEventHandlers(restProps.onBlur, inputProps.onBlur);
      const onInvalid = useCombineEventHandlers(restProps.onInvalid, inputProps.onInvalid);
      const onKeyPress = useCombineEventHandlers(restProps.onKeyPress, inputProps.onKeyPress);

      const defaultSize = useFieldSize();
      const fieldSize = size ?? defaultSize;

      const field = (
        <Field
          as={Theme.Transparent}
          type="number"
          {...restProps}
          {...inputProps}
          size={fieldSize}
          onChange={onChange}
          onBlur={onBlur}
          onInvalid={onInvalid}
          disabled={disabled}
          onKeyPress={onKeyPress}
          ref={ref}
          css={{ padding: { left: compact ? t => t.sharedSpacing.s : 0, right: 0 } }}
          {...NnTestIdProps.InputNumberField}
        />
      );

      return (
        <styled.Div>
          {compact ? (
            <styled.Div
              css={{
                display: 'grid',
                gridTemplateAreas: `"${Area.Field} ${Area.Add}" "${Area.Field} ${Area.Reduce}"`,
                gridTemplateColumns: 'minmax(0, 1fr) auto',
              }}
            >
              {field}
              <Add
                as={as}
                onClick={add}
                aria-label={translate(t => t.miniCart.increaseItem)}
                disabled={disabled}
                css={createCompactSpinnerDecorator(fieldSize)}
                {...NnTestIdProps.ProductQuantityIncrease}
              >
                <ArrowUp css={iconDecorator} />
              </Add>
              <Reduce
                as={as}
                onClick={reduce}
                aria-label={translate(t => t.miniCart.reduceItem)}
                disabled={disabled}
                css={createCompactSpinnerDecorator(fieldSize)}
                {...NnTestIdProps.ProductQuantityDecrease}
              >
                <ArrowDown css={iconDecorator} />
              </Reduce>
            </styled.Div>
          ) : (
            <styled.Div
              css={{
                display: 'grid',
                gridTemplateAreas: `"${Area.Reduce} ${Area.Field} ${Area.Add}"`,
                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
              }}
            >
              {field}
              <Add
                size={fieldSize}
                disabled={disabled}
                as={as}
                aria-label={translate(t => t.miniCart.increaseItem)}
                css={createAddReduceDecorator(fieldSize)}
                onClick={add}
                {...NnTestIdProps.ProductQuantityIncrease}
              >
                <Plus css={iconDecorator} />
              </Add>
              <Reduce
                size={fieldSize}
                disabled={disabled}
                as={as}
                aria-label={translate(t => t.miniCart.reduceItem)}
                css={createAddReduceDecorator(fieldSize)}
                onClick={reduce}
                {...NnTestIdProps.ProductQuantityDecrease}
              >
                <Minus css={iconDecorator} />
              </Reduce>
            </styled.Div>
          )}
          <ValidationMessage visible={invalid}>{validationMessage}</ValidationMessage>
        </styled.Div>
      );
    },
  ),
);

const enum Area {
  Field = 'field',
  Add = 'add',
  Reduce = 'reduce',
}

const disableDecorator = styled({
  ':disabled': { opacity: 0.5, cursor: 'not-allowed' },
});

const Field = styled(
  Input,
  disableDecorator,
  {
    gridArea: Area.Field,
    textAlign: 'center',
    borderRadius: 0, // Needed for Safari
    appearance: 'textfield', // Needed for Firefox to drop the input spinners
    fontSize: 'inherit',
  },
  selector(['::-webkit-inner-spin-button', '::-webkit-outer-spin-button'], {
    WebkitAppearance: 'none',
    margin: { xy: 0 },
  }),
);

const Add = styled(Button, disableDecorator, {
  gridArea: Area.Add,
  display: 'flex',
  alignItems: 'center',
  fontSize: t => t.sharedFontSize.xxs,
});
const Reduce = styled(Add, { gridArea: Area.Reduce });

const iconDecorator = styled({
  fontSize: 12,
});

function createAddReduceDecorator(size: FieldSizeType) {
  return styled({
    padding: {
      x: (t): number => {
        switch (size) {
          case FieldSize.XXSmall:
          case FieldSize.XSmall:
          case FieldSize.Small:
            return t.sharedSpacing.xxs;
          case FieldSize.Default:
          case FieldSize.Large:
          case FieldSize.XLarge:
            return t.sharedSpacing.xs;
        }
      },
    },
  });
}

export function createCompactSpinnerDecorator(size: FieldSizeType) {
  return styled({
    padding: {
      x: (t): number => {
        switch (size) {
          case FieldSize.XXSmall:
            return t.sharedSpacing.xxs;
          case FieldSize.XSmall:
            return t.sharedSpacing.xxs;
          case FieldSize.Small:
            return t.sharedSpacing.xs;
          case FieldSize.Default:
            return t.sharedSpacing.m;
          case FieldSize.Large:
            return t.sharedSpacing.xs;
          case FieldSize.XLarge:
            return t.sharedSpacing.l;
        }
      },
      y: 0,
    },
    fontSize: (t): number => {
      switch (size) {
        case FieldSize.XXSmall:
          return t.sharedFontSize.xxs;
        case FieldSize.XSmall:
          return t.sharedFontSize.xxs;
        case FieldSize.Small:
          return t.sharedFontSize.xxs;
        case FieldSize.Default:
          return t.sharedFontSize.xs;
        case FieldSize.Large:
          return t.sharedFontSize.s;
        case FieldSize.XLarge:
          return t.sharedFontSize.s;
      }
    },
  });
}
